import React, { useEffect, useState } from 'react';
import { AdminLayout } from '../components/AdminLayout';
import { useApp } from '../context/AppContext';
import { Zap, Flame, Droplets, Trash2, CheckCheck, Eye, Calendar, Home, User, Phone } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const AdminUtilityIndexPage = () => {
  const { getAuthHeaders } = useApp();
  const [indexes, setIndexes] = useState([]);
  const [selectedApartment, setSelectedApartment] = useState('all');
  const [apartments, setApartments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [indexRes, aptRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/utility-index`, { headers: getAuthHeaders() }),
        axios.get(`${BACKEND_URL}/api/apartments`)
      ]);
      setIndexes(indexRes.data);
      setApartments(aptRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await axios.put(`${BACKEND_URL}/api/utility-index/mark-all-read`, {}, {
        headers: getAuthHeaders()
      });
      toast.success('Toate indexurile marcate ca citite');
      fetchData();
    } catch (error) {
      toast.error('Failed to mark as read');
    }
  };

  const handleMarkRead = async (id) => {
    try {
      await axios.put(`${BACKEND_URL}/api/utility-index/${id}/read`, {}, {
        headers: getAuthHeaders()
      });
      setIndexes(indexes.map(idx => idx.id === id ? { ...idx, read: true } : idx));
    } catch (error) {
      toast.error('Failed to mark as read');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sigur doriți să ștergeți acest index?')) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/utility-index/${id}`, {
        headers: getAuthHeaders()
      });
      toast.success('Index șters');
      setIndexes(indexes.filter(idx => idx.id !== id));
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const filteredIndexes = selectedApartment === 'all' 
    ? indexes 
    : indexes.filter(idx => idx.apartment_id === selectedApartment);

  const unreadCount = indexes.filter(idx => !idx.read).length;

  // Group by apartment for history view
  const groupedByApartment = apartments.map(apt => ({
    ...apt,
    indexes: indexes.filter(idx => idx.apartment_id === apt.id)
  })).filter(apt => apt.indexes.length > 0);

  return (
    <AdminLayout>
      <div data-testid="admin-utility-index-page">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <h1 className="text-4xl font-serif">Indexuri Utilități</h1>
            {unreadCount > 0 && (
              <span className="bg-red-500 text-white text-sm font-bold px-3 py-1 rounded-full animate-pulse">
                {unreadCount} noi
              </span>
            )}
          </div>
          <div className="flex gap-2">
            {unreadCount > 0 && (
              <Button onClick={handleMarkAllRead} variant="outline" size="sm">
                <CheckCheck size={16} className="mr-2" />
                Marchează toate ca citite
              </Button>
            )}
          </div>
        </div>

        {/* Filter by apartment */}
        <div className="mb-6">
          <select
            value={selectedApartment}
            onChange={(e) => setSelectedApartment(e.target.value)}
            className="px-4 py-2 bg-slate-900 border border-white/10 rounded focus:border-primary focus:outline-none"
          >
            <option value="all">Toate apartamentele ({indexes.length})</option>
            {apartments.map(apt => {
              const count = indexes.filter(idx => idx.apartment_id === apt.id).length;
              return (
                <option key={apt.id} value={apt.id}>
                  {apt.title?.ro || apt.title?.en} ({count})
                </option>
              );
            })}
          </select>
        </div>

        {loading ? (
          <div className="text-center py-12">Se încarcă...</div>
        ) : filteredIndexes.length === 0 ? (
          <div className="glass p-12 text-center">
            <p className="text-muted-foreground">Nu există indexuri înregistrate.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredIndexes.map((idx) => (
              <div 
                key={idx.id} 
                className={`glass p-4 ${!idx.read ? 'border-l-4 border-red-500 bg-red-500/5' : ''}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <Home size={18} className="text-primary" />
                      <span className="font-semibold">{idx.apartment_title || 'Apartament'}</span>
                      {!idx.read && (
                        <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded">NOU</span>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-3 gap-4 mb-3">
                      {idx.electric_index !== null && (
                        <div className="flex items-center gap-2 bg-yellow-500/10 px-3 py-2 rounded">
                          <Zap size={16} className="text-yellow-400" />
                          <span className="text-sm">
                            <span className="text-muted-foreground">Electric:</span>{' '}
                            <strong>{idx.electric_index}</strong> kWh
                          </span>
                        </div>
                      )}
                      {idx.gas_index !== null && (
                        <div className="flex items-center gap-2 bg-orange-500/10 px-3 py-2 rounded">
                          <Flame size={16} className="text-orange-400" />
                          <span className="text-sm">
                            <span className="text-muted-foreground">Gaz:</span>{' '}
                            <strong>{idx.gas_index}</strong> m³
                          </span>
                        </div>
                      )}
                      {idx.water_index !== null && (
                        <div className="flex items-center gap-2 bg-blue-500/10 px-3 py-2 rounded">
                          <Droplets size={16} className="text-blue-400" />
                          <span className="text-sm">
                            <span className="text-muted-foreground">Apă:</span>{' '}
                            <strong>{idx.water_index}</strong> m³
                          </span>
                        </div>
                      )}
                    </div>

                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      {idx.name && (
                        <span className="flex items-center gap-1">
                          <User size={14} /> {idx.name}
                        </span>
                      )}
                      {idx.phone && (
                        <span className="flex items-center gap-1">
                          <Phone size={14} /> {idx.phone}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <Calendar size={14} />
                        {new Date(idx.created_at).toLocaleString('ro-RO')}
                      </span>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    {!idx.read && (
                      <Button onClick={() => handleMarkRead(idx.id)} variant="outline" size="sm">
                        <Eye size={14} />
                      </Button>
                    )}
                    <Button onClick={() => handleDelete(idx.id)} variant="destructive" size="sm">
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* History by Apartment */}
        {groupedByApartment.length > 0 && (
          <div className="mt-12">
            <h2 className="text-2xl font-serif mb-6">Istoric pe Apartamente</h2>
            <div className="space-y-6">
              {groupedByApartment.map(apt => (
                <div key={apt.id} className="glass p-4">
                  <h3 className="font-semibold mb-4 flex items-center gap-2">
                    <Home size={18} className="text-primary" />
                    {apt.title?.ro || apt.title?.en}
                    <span className="text-sm text-muted-foreground">({apt.indexes.length} înregistrări)</span>
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-white/10">
                          <th className="text-left py-2 px-2">Data</th>
                          <th className="text-right py-2 px-2">
                            <Zap size={14} className="inline text-yellow-400" /> Electric
                          </th>
                          <th className="text-right py-2 px-2">
                            <Flame size={14} className="inline text-orange-400" /> Gaz
                          </th>
                          <th className="text-right py-2 px-2">
                            <Droplets size={14} className="inline text-blue-400" /> Apă
                          </th>
                          <th className="text-left py-2 px-2">Trimis de</th>
                        </tr>
                      </thead>
                      <tbody>
                        {apt.indexes.map(idx => (
                          <tr key={idx.id} className="border-b border-white/5 hover:bg-white/5">
                            <td className="py-2 px-2">
                              {new Date(idx.created_at).toLocaleDateString('ro-RO')}
                            </td>
                            <td className="text-right py-2 px-2 font-mono">
                              {idx.electric_index !== null ? `${idx.electric_index} kWh` : '-'}
                            </td>
                            <td className="text-right py-2 px-2 font-mono">
                              {idx.gas_index !== null ? `${idx.gas_index} m³` : '-'}
                            </td>
                            <td className="text-right py-2 px-2 font-mono">
                              {idx.water_index !== null ? `${idx.water_index} m³` : '-'}
                            </td>
                            <td className="py-2 px-2 text-muted-foreground">
                              {idx.name || idx.phone || '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminUtilityIndexPage;
